import React, {
  useCallback,
} from 'react';
import {
  shallowEqual,
  useSelector,
} from 'react-redux';
import {
  Link,
} from 'react-router';

import {
  coreSelectors,
  ResourceType,
} from '@gymcloud/core';

import {
  Modal,
  Button,
  Accordion,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

type Props = {
  id: number,
  isOpen: boolean;
  resourceType: ResourceType,
  onRequestClose: () => void;
};

const ReviewProductModal = ({
  id,
  isOpen,
  resourceType,
  onRequestClose,
}: Props) => {
  const mapState = useCallback((state) => {
    const basicInfo = coreSelectors.getResourceById(resourceType, id)(state);

    const programsMap = coreSelectors.getResourceMap('programTemplates')(state);
    const workoutsMap = coreSelectors.getResourceMap('workoutTemplates')(state);
    const documentsMap = coreSelectors.getResourceMap('documents')(state);

    return {
      basicInfo,
      programs: basicInfo?.program_template_ids?.length
        ? basicInfo.program_template_ids.map(i => programsMap[i])
        : [],
      workouts: basicInfo?.workout_template_ids?.length
        ? basicInfo.workout_template_ids.map(i => workoutsMap[i])
        : [],
      documents: basicInfo?.document_ids?.length
        ? basicInfo.document_ids.map(i => documentsMap[i])
        : [],
    };
  }, [id, resourceType]);
  const {
    basicInfo,
    programs,
    workouts,
    documents,
  } = useSelector(mapState, shallowEqual);

  if (!basicInfo) return null;

  return (
    <Modal
      title="Review Product"
      contentStyle={{
        width: '800px',
      }}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
    >
      <Flex
        column
        spacing={30}
        width="100%"
      >
        <Flex
          column
          spacing={10}
          width="100%"
        >
          <Text
            size="1.28em"
            weight="600"
            whiteSpace="pre-line"
          >
            {basicInfo.name}
          </Text>

          {basicInfo.description && (
            <Text
              whiteSpace="pre-line"
            >
              {basicInfo.description}
            </Text>
          )}
          <Flex align="baseline" spacing={5}>
            <Text weight="600" size="1.28em">
              $
              {basicInfo.amount || 0}
            </Text>

            {basicInfo.interval && (
              <>
                <Text>
                  {basicInfo.interval === 'year' ? 'annually,' : 'per month,'}
                </Text>

                <Text weight="600" size="1.28em">
                  {`${basicInfo.trial_period_days || 0} day${basicInfo.trial_period_days > 1 ? 's' : ''}`}
                </Text>

                <Text>
                  trial period
                </Text>
              </>
            )}
          </Flex>

          {basicInfo?.slug && (
            <Link to={`/${basicInfo.slug}`}>
              <Text color="link">
                {`${window.location.origin}/${basicInfo.slug}`}
              </Text>
            </Link>
          )}
        </Flex>

        <Accordion
          contents={[
            {
              title: 'Programs',
              subTitle: programs.length ? `(${programs.length})` : null,
              inner: () => programs.map(
                program => (
                  <div key={program.id}>
                    {program.name}
                  </div>
                ),
              ),
            },
            {
              title: 'Workouts',
              subTitle: workouts.length ? `(${workouts.length})` : null,
              inner: () => workouts.map(
                workout => (
                  <div key={workout.id}>
                    {workout.name}
                  </div>
                ),
              ),
            },
            {
              title: 'Documents',
              subTitle: documents.length ? `(${documents.length})` : null,
              inner: () => documents.map(
                document => (
                  <div key={document.id}>
                    {document.name}
                  </div>
                ),
              ),
            },
          ]}
        />

        <Flex
          width="100%"
          justify="flex-end"
          margin="30px 0 0 0"
        >
          <Button
            ghost
            width="140px"
            spacing="large"
            appearance="danger"
            onClick={onRequestClose}
          >
            Cancel
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ReviewProductModal;
